import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Radio, 
  QrCode, 
  Clock, 
  Check, 
  X, 
  MoreHorizontal, 
  ArrowUp, 
  ArrowDown, 
  UserX, 
  Square,
  Mic,
  Users,
  Music,
  Play,
  Pause,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Tv,
  Maximize2,
  ScrollText,
  Smartphone,
  Heart,
  Star,
  DollarSign,
  Settings
} from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';

const KaraokeLDTour = ({ onComplete, onSkip }) => {
  const { format } = useCurrency();
  const [currentStep, setCurrentStep] = useState(0);
  const [simulatedRequests, setSimulatedRequests] = useState([
    { id: 1, song: 'Bohemian Rhapsody', artist: 'Queen', requester: 'Sarah', status: 'pending', note: 'Love this song!' },
    { id: 2, song: 'Sweet Caroline', artist: 'Neil Diamond', requester: 'Mike', status: 'pending' },
    { id: 3, song: 'Don\'t Stop Believin\'', artist: 'Journey', requester: 'Emma', status: 'approved', position: 1 },
    { id: 4, song: 'Wonderwall', artist: 'Oasis', requester: 'David', status: 'approved', position: 2 }
  ]);

  const steps = [
    {
      title: "Welcome to Your Karaoke Live Dashboard! 🎤",
      description: "Let's take an interactive tour of your karaoke management system.",
      icon: <Mic className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mic className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-purple-100 mb-2">Ready to Rock the Mic?</h3>
            <p className="text-purple-300">
              This interactive tour will show you exactly how to manage your karaoke sessions like a pro.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4 text-center">
              <Radio className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Go Live</p>
              <p className="text-purple-400 text-sm">Start your session</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4 text-center">
              <QrCode className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Share</p>
              <p className="text-purple-400 text-sm">QR codes & URLs</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4 text-center">
              <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Review</p>
              <p className="text-purple-400 text-sm">Approve requests</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4 text-center">
              <Tv className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Display</p>
              <p className="text-purple-400 text-sm">TV & announcements</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 1: Go Live & Activate Your Session 🎬",
      description: "Start your karaoke session and watch your request page come to life.",
      icon: <Radio className="w-12 h-12 text-red-400" />,
      content: (
        <div className="space-y-6">
          {/* Simulated Session Control */}
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-purple-100 font-semibold text-lg">Session Control</h4>
                <p className="text-purple-300 text-sm">Start and manage your karaoke session</p>
              </div>
              <Badge className="bg-red-600 hover:bg-red-700">
                <Radio className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            </div>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-3">
                <Radio className="w-5 h-5 mr-2" />
                Go Live
              </Button>
            </motion.div>
          </div>

          {/* Simulated Request Page Activation */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1 }}
            className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-600/50 rounded-lg p-6"
          >
            <div className="flex items-center space-x-4 mb-4">
              <Smartphone className="w-8 h-8 text-blue-400" />
              <div>
                <h4 className="text-blue-100 font-semibold">Request Page Activated!</h4>
                <p className="text-blue-300 text-sm">Your audience can now request songs</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <QrCode className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                <p className="text-blue-200 font-semibold">QR Code</p>
                <p className="text-blue-300 text-sm">Scan with phone</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <ExternalLink className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                <p className="text-blue-200 font-semibold">Direct Link</p>
                <p className="text-blue-300 text-sm">yoursongrequest.com/karaoke/dj-name</p>
              </div>
            </div>
          </motion.div>
        </div>
      )
    },
    {
      title: "Step 2: Configure Paid Requests ⭐",
      description: "Set up optional paid request features to enhance your karaoke experience.",
      icon: <Star className="w-12 h-12 text-yellow-400" />,
      content: (
        <div className="space-y-6">
          {/* Simulated Paid Request Settings */}
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-yellow-200 font-semibold text-lg">Paid Request Settings</h4>
                <p className="text-yellow-300 text-sm">Optional feature to allow audience members to skip the queue</p>
              </div>
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <h5 className="text-purple-100 font-semibold">Enable Paid Requests</h5>
              </div>
              <p className="text-purple-300 text-sm mb-3">
                Toggle this feature on/off. When enabled, audience members can pay to skip the queue.
              </p>
              <div className="bg-purple-800/50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <span className="text-purple-200 text-sm">Paid Requests</span>
                  <div className="w-12 h-6 bg-green-600 rounded-full relative">
                    <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <h5 className="text-purple-100 font-semibold">Skip Queue Price</h5>
              </div>
              <p className="text-purple-300 text-sm mb-3">
                Set the price for audience members to skip the queue.
              </p>
              <div className="bg-purple-800/50 rounded-lg p-2">
                <span className="text-purple-200 font-mono">{format(5)}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-4 h-4 text-blue-400" />
              <h5 className="text-purple-100 font-semibold">Request Limit</h5>
            </div>
            <p className="text-purple-300 text-sm mb-3">
              Set the maximum number of paid requests that can be active at any time.
            </p>
            <div className="bg-purple-800/50 rounded-lg p-2">
              <span className="text-purple-200 font-mono">3 paid requests maximum</span>
            </div>
          </div>
          
          <p className="text-purple-300 text-sm">
            <strong>How it works:</strong> When enabled, audience members will see a "Skip the Queue" option when submitting requests. 
            Paid requests automatically go to the top of your queue and are marked with a star icon.
          </p>
        </div>
      )
    },
    {
      title: "Step 3: See How Your Audience Requests Songs 📱",
      description: "Watch how your audience submits requests from their mobile phones.",
      icon: <Smartphone className="w-12 h-12 text-green-400" />,
      content: (
        <div className="space-y-6">
          {/* Mobile Phone Simulation */}
          <div className="bg-gradient-to-b from-green-900/30 to-blue-900/30 border border-green-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Smartphone className="w-8 h-8 text-green-400" />
              <div>
                <h4 className="text-green-100 font-semibold">Your Audience's Mobile Experience</h4>
                <p className="text-green-300 text-sm">How requests look on their phones</p>
              </div>
            </div>
            
            {/* Simulated Mobile Phone */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-black rounded-3xl p-2 mx-auto w-64"
            >
              <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-4 min-h-96">
                {/* Mobile Header */}
                <div className="text-center mb-4">
                  <h3 className="text-white font-bold text-lg">🎤 Karaoke Requests</h3>
                  <p className="text-purple-300 text-sm">Submit your song request</p>
                </div>
                
                {/* Mobile Form */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="space-y-3"
                >
                  <div className="bg-white/10 rounded-lg p-3">
                    <label className="text-purple-200 text-xs">Song Title</label>
                    <input 
                      type="text" 
                      placeholder="Sweet Caroline" 
                      className="w-full bg-transparent text-white placeholder-purple-400 text-sm mt-1"
                      readOnly
                    />
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-3">
                    <label className="text-purple-200 text-xs">Original Artist</label>
                    <input 
                      type="text" 
                      placeholder="Neil Diamond" 
                      className="w-full bg-transparent text-white placeholder-purple-400 text-sm mt-1"
                      readOnly
                    />
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-3">
                    <label className="text-purple-200 text-xs">Your Name</label>
                    <input 
                      type="text" 
                      placeholder="Sarah" 
                      className="w-full bg-transparent text-white placeholder-purple-400 text-sm mt-1"
                      readOnly
                    />
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-3">
                    <label className="text-purple-200 text-xs">Note (Optional)</label>
                    <textarea 
                      placeholder="Love this song!" 
                      className="w-full bg-transparent text-white placeholder-purple-400 text-sm mt-1 resize-none"
                      rows="2"
                      readOnly
                    />
                  </div>
                  
                  {/* Skip Queue Option */}
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3"
                  >
                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400" />
                      <span className="text-yellow-200 text-sm">Skip the Queue ({format(5)})</span>
                    </div>
                    <p className="text-yellow-300 text-xs mt-1">Get priority placement in the queue</p>
                  </motion.div>
                  
                  {/* Submit Button */}
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.5 }}
                  >
                    <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold">
                      Send Request
                    </button>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
          
          <p className="text-purple-300 text-sm text-center">
            Your audience uses this simple form to submit their song requests. 
            They can also choose to skip the queue for a small fee!
          </p>
        </div>
      )
    },
    {
      title: "Step 4: Watch Requests Appear in Your Dashboard 📊",
      description: "See how song requests appear in real-time in your DJ dashboard.",
      icon: <Clock className="w-12 h-12 text-yellow-400" />,
      content: (
        <div className="space-y-6">
          {/* Simulated Request Coming In */}
          <div className="bg-gradient-to-b from-green-900/30 to-blue-900/30 border border-green-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Clock className="w-8 h-8 text-green-400" />
              <div>
                <h4 className="text-green-100 font-semibold">New Request Coming In...</h4>
                <p className="text-green-300 text-sm">Simulating real-time request</p>
              </div>
            </div>
            
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 rounded-lg p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">S</span>
                </div>
                <div className="flex-1">
                  <p className="text-green-200 font-semibold">Sweet Caroline</p>
                  <p className="text-green-300 text-sm">by Neil Diamond</p>
                  <p className="text-green-400 text-xs">Requested by Sarah</p>
                  <p className="text-green-400 text-xs italic">"Love this song!"</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-yellow-600 text-yellow-100">New</Badge>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Simulated Pending Requests */}
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-semibold mb-4">Pending Requests</h4>
            <div className="space-y-3">
              {simulatedRequests.filter(req => req.status === 'pending').map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.2 }}
                  className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-100 font-medium">{request.song}</p>
                      <p className="text-yellow-300 text-sm">by {request.artist}</p>
                      <p className="text-yellow-400 text-xs">Requested by {request.requester}</p>
                      {request.note && (
                        <p className="text-yellow-400 text-xs italic">"{request.note}"</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="destructive">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 5: Approve & Manage Your Queue 📋",
      description: "Learn how to approve requests and manage your performance queue.",
      icon: <Music className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-6">
          {/* Simulated Queue Management */}
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-semibold mb-4">Incoming Request Queue</h4>
            <div className="space-y-3">
              {simulatedRequests.filter(req => req.status === 'approved').map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.2 }}
                  className="bg-purple-800/30 border border-purple-600/50 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{request.position}</span>
                      </div>
                      <div>
                        <p className="text-purple-100 font-medium">{request.song}</p>
                        <p className="text-purple-300 text-sm">by {request.artist}</p>
                        <p className="text-purple-400 text-xs">Requested by {request.requester}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Done
                      </Button>
                      <Button size="sm" variant="outline">
                        <MoreHorizontal className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Queue Management Options */}
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-semibold mb-4">Queue Management Options</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <ArrowUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-green-200 font-semibold">Move Up</p>
                <p className="text-green-300 text-xs">Prioritize song</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <ArrowDown className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-blue-200 font-semibold">Move Down</p>
                <p className="text-blue-300 text-xs">Lower priority</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <UserX className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-red-200 font-semibold">Remove</p>
                <p className="text-red-300 text-xs">Delete from queue</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <Radio className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-yellow-200 font-semibold">Announce</p>
                <p className="text-yellow-300 text-xs">Up next on TV</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 6: TV Display & Announcements 📺",
      description: "See how your TV display works and how to announce upcoming songs.",
      icon: <Tv className="w-12 h-12 text-blue-400" />,
      content: (
        <div className="space-y-6">
          {/* Simulated TV Display */}
          <div className="bg-black border border-blue-600/50 rounded-lg p-6">
            <div className="text-center mb-4">
              <Tv className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <h4 className="text-blue-100 font-semibold">TV Display</h4>
              <p className="text-blue-300 text-sm">What your audience sees</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-lg p-4 mb-4">
              <div className="text-center">
                <div className="text-2xl mb-2">🎤</div>
                <h3 className="text-white font-bold text-lg mb-1">Live Karaoke Session</h3>
                <p className="text-blue-200 text-sm">Current Queue</p>
              </div>
            </div>
            
            <div className="space-y-2">
              {simulatedRequests.filter(req => req.status === 'approved').slice(0, 3).map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.2 }}
                  className="bg-white/10 rounded-lg p-3"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xs">{request.position}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{request.song}</p>
                      <p className="text-blue-200 text-sm">by {request.artist}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Announcement Feature */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1 }}
            className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-600/50 rounded-lg p-6"
          >
            <div className="text-center">
              <Star className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
              <h4 className="text-yellow-100 font-semibold text-lg mb-2">Announce Up Next!</h4>
              <p className="text-yellow-300 text-sm mb-4">Create excitement with animated announcements</p>
              
              <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">🎵</div>
                <h3 className="text-white font-bold text-xl">Don't Stop Believin'</h3>
                <p className="text-yellow-100 text-lg">by Journey</p>
                <p className="text-yellow-200 text-sm">Requested by Emma</p>
                <div className="flex justify-center space-x-2 mt-3">
                  <span className="text-2xl">🎤</span>
                  <span className="text-2xl">🎵</span>
                  <span className="text-2xl">🎤</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )
    },
    {
      title: "Step 7: Mark Songs Complete & Track Progress ✅",
      description: "Learn how to mark songs as performed and track your session statistics.",
      icon: <Check className="w-12 h-12 text-green-400" />,
      content: (
        <div className="space-y-6">
          {/* Song Completion */}
          <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Check className="w-8 h-8 text-green-400" />
              <div>
                <h4 className="text-green-100 font-semibold">Mark Song as Performed</h4>
                <p className="text-green-300 text-sm">Complete songs and track progress</p>
              </div>
            </div>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-green-200 font-medium">Don't Stop Believin'</p>
                    <p className="text-green-300 text-sm">by Journey</p>
                    <p className="text-green-400 text-xs">Requested by Emma</p>
                  </div>
                </div>
                <Badge className="bg-green-600 text-green-100">Completed</Badge>
              </div>
            </motion.div>
          </div>

          {/* Session Statistics */}
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-semibold mb-4">Session Statistics</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-100">8</div>
                <p className="text-blue-200 font-semibold">Total Requests</p>
                <p className="text-blue-300 text-xs">Approved & denied</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Music className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-100">3</div>
                <p className="text-green-200 font-semibold">Songs Performed</p>
                <p className="text-green-300 text-xs">Marked as done</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Heart className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-red-100">{format(0)}</div>
                <p className="text-red-200 font-semibold">Total Earnings</p>
                <p className="text-red-300 text-xs">Skip queue payments</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-100">45m</div>
                <p className="text-yellow-200 font-semibold">Session Time</p>
                <p className="text-yellow-300 text-xs">Current duration</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 8: End Your Session & Clean Up 🏁",
      description: "Learn how to properly end your session and clean up for the next one.",
      icon: <Square className="w-12 h-12 text-red-400" />,
      content: (
        <div className="space-y-6">
          {/* Session End Process */}
          <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Square className="w-8 h-8 text-red-400" />
              <div>
                <h4 className="text-red-100 font-semibold">End Session</h4>
                <p className="text-red-300 text-sm">Wrap up your karaoke session</p>
              </div>
            </div>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-3">
                <Square className="w-5 h-5 mr-2" />
                End Session
              </Button>
            </motion.div>
          </div>

          {/* Countdown Timer */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1 }}
            className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
              <div>
                <h4 className="text-yellow-100 font-semibold">Session Ending</h4>
                <p className="text-yellow-300 text-sm">5-second countdown with undo option</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-100 mb-2">5</div>
              <p className="text-yellow-300 text-sm mb-4">Your session will end in 5 seconds</p>
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                <Radio className="w-4 h-4 mr-2" />
                Stay Online
              </Button>
            </div>
          </motion.div>

          {/* Cleanup Summary */}
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-semibold mb-4">Session Cleanup</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-purple-200">All session data cleared</span>
              </div>
              <div className="flex items-center space-x-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-purple-200">Request page deactivated</span>
              </div>
              <div className="flex items-center space-x-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-purple-200">TV display stopped</span>
              </div>
              <div className="flex items-center space-x-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-purple-200">Ready for next session</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "You're Ready to Rock! 🎤✨",
      description: "Congratulations! You now know how to run amazing karaoke sessions.",
      icon: <Star className="w-12 h-12 text-yellow-400" />,
      content: (
        <div className="space-y-6 text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="w-32 h-32 bg-gradient-to-br from-purple-600 via-pink-600 to-yellow-600 rounded-full flex items-center justify-center mx-auto"
          >
            <Star className="w-16 h-16 text-white" />
          </motion.div>
          
          <div>
            <h3 className="text-3xl font-bold text-purple-100 mb-4">You're All Set!</h3>
            <p className="text-purple-300 text-lg mb-6">
              You've learned how to run professional karaoke sessions with Your Song Request.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <Radio className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Go Live</p>
              <p className="text-purple-300 text-sm">Start your session</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <QrCode className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Share</p>
              <p className="text-purple-300 text-sm">QR codes & links</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <Tv className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-purple-200 font-semibold">Display</p>
              <p className="text-purple-300 text-sm">TV & announcements</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-600/50 rounded-lg p-6">
            <h4 className="text-green-100 font-semibold mb-2">Ready to Start Your First Session?</h4>
            <p className="text-green-300 text-sm">
              Click "Start Karaoke Session" to begin your journey as a professional karaoke DJ!
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-purple-800/50 border-b border-purple-600/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {steps[currentStep].icon}
              <div>
                <h2 className="text-2xl font-bold text-white">{steps[currentStep].title}</h2>
                <p className="text-purple-300">{steps[currentStep].description}</p>
              </div>
            </div>
            <Button
              onClick={onSkip}
              variant="ghost"
              size="sm"
              className="text-purple-300 hover:text-white hover:bg-purple-700/50"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-purple-800/30 px-6 py-3">
          <div className="flex items-center justify-between text-sm text-purple-300 mb-2">
            <span>Step {currentStep + 1} of {steps.length}</span>
            <span>{Math.round(((currentStep + 1) / steps.length) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-purple-700/30 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {steps[currentStep].content}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="bg-purple-800/50 border-t border-purple-600/30 p-6">
          <div className="flex items-center justify-between">
            <Button
              onClick={onSkip}
              variant="outline"
              className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
            >
              Skip Tour
            </Button>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={prevStep}
                disabled={currentStep === 0}
                variant="outline"
                className="border-purple-600 text-purple-300 hover:bg-purple-600/20 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              <Button
                onClick={nextStep}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <Radio className="w-4 h-4 mr-2" />
                    Start Karaoke Session
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default KaraokeLDTour;
